import { FULL_BODY_CATEGORY_KEYS } from '@/constants';
import Blizzard from '@/services/Blizzard';
import CategoryAssets from '@/services/CategoryAssets';
import SendClientMessage from '@/services/SendClientMessage';
import BadgingHelper from '@/services/BadgingHelper';
import MetricsHelper from '@/services/MetricsHelper';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';
import { ActionTree } from 'vuex';
import { OutfitSharingStateEnum } from '@/enum/outfit_sharing_state';

const actions: ActionTree<RootState, RootState> = {
  loadAssetsData({ state, commit }) {
    CategoryAssets.loadAssetsData(
      state.initialTraits.gender,
      state.initialTraits.style,
      state.flowMode,
      state.assetOverrides,
    ).then((assetsData) => {
      commit('loadAssetsData', assetsData);
      commit('mergeDefaultsWithInitialTraits', assetsData.defaults);
      commit('setMixAndMatchDefaultOutfit');
      commit('setColourPalettesForSelectedOutfitPieces');
      commit('setColourOptionsForSelectedTraitPieces');
      commit('tuckControlSetForCurrentOutfit');
      commit('filteredSixthBodyOutfits');
      commit('checkForIOS12');
    });
  },
  saveAvatar({ state, commit, dispatch, getters }) {
    dispatch('flushPromotionsData').then(() => {
      if (!state.isSavingAvatar) {
        if (DetailedOutfitHelper.isOutfitChanged(state.initialTraits, state.selectedTraits)) {
          MetricsHelper.logOutfitChange(
            state.selectedTraits,
            state.outfitSelectionType,
            state.hasTappedMixAndMatchItemInSession,
            state.flowMode,
          );
        }
      }
      const currentCategory = getters.selectedCategory || 'none';
      Blizzard.recordExit(currentCategory, true);
      SendClientMessage.saveAvatar(
        state.selectedTraits,
        state.traitOrigins,
        state.flowMode,
        currentCategory,
      );
      commit('setIsSavingAvatar', { isSavingAvatar: true });
    });
  },
  shareOutfit({ state, commit, getters }) {
    SendClientMessage.shareOutfit(state.selectedTraits, state.flowMode);
    commit('setOutfitSharingState', { outfitSharingState: OutfitSharingStateEnum.LOADING });
  },
  exitBuilder({ dispatch, getters }) {
    const currentCategory = getters.selectedCategory || 'none';
    Blizzard.recordExit(currentCategory, false);
    dispatch('flushPromotionsData').then(() => {
      SendClientMessage.exitBuilder(currentCategory);
    });
  },
  goToNextCategory({ state, commit }) {
    const newIndex = state.selectedCategoryIndex + 1;

    // Do not go beyond the last category
    if (newIndex > state.categories.length - 1) {
      return;
    }
    commit('setNewCategoryIndex', {
      index: newIndex,
    });
    commit('scrollToPage', { index: newIndex });
  },
  goToPrevCategory({ state, commit }) {
    const newIndex = state.selectedCategoryIndex - 1;

    // Do not go beyond the first category
    if (newIndex < 0) {
      return;
    }
    commit('setNewCategoryIndex', {
      index: newIndex,
    });
    commit('scrollToPage', { index: newIndex });
  },
  categoryScrollTransitionEnd({ state, commit, dispatch }) {
    // This action is fired by the SnappablePagerMixin to notify the store that
    // the category scrolling animation is complete. Before updating the
    // `selectedCategoryIndex` the store has to check if the next category is
    // a full body category and based on that, either go through with updating
    // the `selectedCategoryIndex` or not. The reason for this check is to ensure
    // that the `selectedCategoryIndex` isn't updated at the same time as the full body
    // animation is taking place.
    // This check is required so there is no risk of slowing down the animation.
    let isAnimateFullBody = false;
    if (state.categories[state.newCategoryIndex]) {
      // Get the next category's key to check if it's a full body category
      const nextCategoryKey = state.categories[state.newCategoryIndex]?.key;
      if (nextCategoryKey) {
        const isNextCategoryFullBody = FULL_BODY_CATEGORY_KEYS.includes(nextCategoryKey);

        // Only update when the next value is different than the previous to avoid
        // unnecessary store updates. Use this flag to notify the subscribers that
        // the next category will be a full body category.
        isAnimateFullBody = isNextCategoryFullBody !== state.isFullBodyCategory;
        if (isAnimateFullBody) {
          commit('setIsFullBodyCategory', {
            isFullBodyCategory: isNextCategoryFullBody,
          });
        }
      }
    }

    // This is used to ensure the dispatcher doesn't also update the `selectedCategoryIndex`
    // while the full body preview is animating.
    if (!isAnimateFullBody) {
      dispatch('updateSelectedCategoryIndex');
    }
  },
  updateSelectedCategoryIndex({ state, commit }) {
    if (state.selectedCategoryIndex === state.newCategoryIndex) {
      return;
    }
    commit('setCategorySelected', {
      index: state.newCategoryIndex,
    });
  },
  loadPromotionsData({ state, commit }) {
    BadgingHelper.loadPromotions(state.flowMode).then((initialPromotions) => {
      commit('loadInitialPromotions', initialPromotions);
    });
  },
  flushPromotionsData({ commit, getters, state }) {
    const exitCategory = getters.selectedCategory;
    if (getters.promotedCategories.includes(exitCategory)) {
      commit('clearStaleCategoryPromotion', exitCategory);
    }
    BadgingHelper.flushPromotions(state.stalePromotions);
  },
};

export default actions;
